import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useOvertimePolicyApi from './config';
import OvertimePolicyDrawer from './OvertimePolicyDrawer';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function OvertimePoliciesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { fetching, columns: baseColumns, data, pagination, create, update, _delete, getById } = useOvertimePolicyApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setEditData(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback(async (row) => {
    try {
      const detail = await getById(row.id);
      setEditData(detail || row);
    } catch {
      setEditData(row);
    }
    setDrawerOpen(true);
  }, [getById]);

  const handleSave = useCallback(async (formData) => {
    const success = editData
      ? await update(editData.id, formData)
      : await create(formData);
    return success;
  }, [editData, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('overtimePolicy.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // "Atama Kuralları": politikanın kime, hangi tarihten itibaren uygulanacağı (AKM POC §6.2) — ayrı sayfa.
  const handleOpenRules = useCallback((row) => {
    history.push(`/overtime-policies/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => {
    if (!canWrite('TA')) return [];
    return [
      { type: 'details', label: t('overtimePolicy.assignmentRules'), onClick: handleOpenRules },
      { type: 'edit', label: t('overtimePolicy.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('common.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete, handleOpenRules]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <OvertimePolicyDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initialValues={editData}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('overtimePolicy.deleteTitle')}
        message={t('overtimePolicy.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('overtimePolicy.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('TA')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('TA') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
